import React, { Component } from "react";
import PropTypes from "prop-types";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";
import { formatNumber, shouldHideField } from "../../../utils/numberFormatting";

import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";

import { functor } from "../utils";

// Short labels for known source paths (single letter)
const SOURCE_PATH_SHORT = {
  open: "A", // Abertura
  high: "H", // High/Máxima
  low: "L", // Low/Mínima
  close: null, // Default - no indicator needed
  hl2: "M", // Mediana
  hlc3: "T", // Típico
  ohlc4: "P", // Ponderada (média)
  hlcc4: "W", // Weighted
};

// Full source path labels for hints
const SOURCE_PATH_FULL = {
  open: "Abertura",
  high: "Máxima",
  low: "Mínima",
  close: "Fechamento",
  hl2: "HL/2 (Mediana)",
  hlc3: "HLC/3 (Típico)",
  ohlc4: "OHLC/4 (Média)",
  hlcc4: "HLCC/4 (Ponderado)",
};

function getSourcePathFullLabel(sourcePath) {
  if (!sourcePath) return SOURCE_PATH_FULL.close;
  const normalized = sourcePath.toLowerCase().trim();
  return SOURCE_PATH_FULL[normalized] || sourcePath;
}

// Returns: { letter: "H", dot: false } or { letter: null, dot: true } for dynamic sources
function getSourceIndicator(sourcePath) {
  if (!sourcePath) return { letter: null, dot: false };
  const normalized = sourcePath.toLowerCase().trim();

  // Known source - use letter (null for close means no indicator)
  if (normalized in SOURCE_PATH_SHORT) {
    return { letter: SOURCE_PATH_SHORT[normalized], dot: false };
  }

  // Dynamic/indicator source - use colored dot
  return { letter: null, dot: true };
}

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

class SingleMAToolTip extends Component {
  constructor(props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }
  handleClick(e) {
    const { onClick, forChart, options } = this.props;
    onClick({ chartId: forChart, ...options }, e);
  }
  render() {
    const {
      textFill,
      labelFill,
      bgFill,
      fontFamily,
      fontSize,
      sourceLetter,
      showSourceDot,
      sourceHint,
    } = this.props;
    const translate =
      "translate(" + this.props.origin[0] + ", " + this.props.origin[1] + ")";

    // Calculate text width for background (add space for indicator if showing)
    const indicatorSpace = sourceLetter || showSourceDot ? 12 : 0;
    const fullText = `${this.props.displayName}${this.props.value}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgPadding = 6;
    const bgWidth = textWidth + 10 + bgPadding * 2 + indicatorSpace; // +10 for the line space

    return (
      <g transform={translate}>
        <title>{`${this.props.displayName}: ${sourceHint}`}</title>
        {/* Background rectangle */}
        {bgFill && (
          <rect
            x={-bgPadding}
            y={-5}
            width={bgWidth}
            height={32}
            fill={bgFill}
            rx={6}
            ry={6}
          />
        )}
        <line
          x1={0}
          y1={2}
          x2={0}
          y2={28}
          stroke={this.props.color}
          strokeWidth="4px"
        />
        <ToolTipText x={5} y={3} fontFamily={fontFamily} fontSize={fontSize}>
          <ToolTipTSpanLabel fill={labelFill}>
            {this.props.displayName}
            {sourceLetter && (
              <tspan fill={this.props.color}> {sourceLetter}</tspan>
            )}
            {showSourceDot && <tspan fill={this.props.color}> ●</tspan>}
          </ToolTipTSpanLabel>
          <tspan x="5" dy="15" fill={textFill}>
            {this.props.value}
          </tspan>
        </ToolTipText>
        <rect
          x={0}
          y={0}
          width={bgWidth}
          height={30}
          onClick={this.handleClick}
          fill="none"
          stroke="none"
          data-evo-id="tooltip-moving-average-1"
        />
      </g>
    );
  }
}

SingleMAToolTip.propTypes = {
  origin: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  fontFamily: PropTypes.string,
  textFill: PropTypes.string,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,
  fontSize: PropTypes.number,
  forChart: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  options: PropTypes.object.isRequired,
  sourceLetter: PropTypes.string,
  showSourceDot: PropTypes.bool,
  sourceHint: PropTypes.string,
};

class MovingAverageTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const { displayValuesFor } = this.props;

    const { chartId } = moreProps;
    const { chartConfig } = moreProps;

    const {
      className,
      onClick,
      width,
      fontFamily,
      fontSize,
      textFill,
      labelFill,
      bgFill,
    } = this.props;
    const { origin: originProp, displayFormat, options } = this.props;
    const {
      chartConfig: { width: chartWidth, height },
    } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);

    const origin = functor(originProp);
    const [x, y] = origin(chartWidth, height);

    return (
      <g transform={`translate(${x}, ${y})`} className={className}>
        {
          options
            .map((each, idx) => {
              const yValue = currentItem && each.yAccessor(currentItem);

              const tooltipLabel = `${each.type} (${each.windowSize})`;
              const yDisplayValue = yValue ? displayFormat(yValue) : "n/a";

              // Hide individual moving average if value is n/a
              if (shouldHideField(yDisplayValue)) {
                return null;
              }

              const { letter: sourceLetter, dot: showSourceDot } =
                getSourceIndicator(each.sourcePath);
              // Use pre-computed sourceLabel if available, fallback to local function
              const sourceHint =
                each.sourceLabel || getSourcePathFullLabel(each.sourcePath);

              return (
                <SingleMAToolTip
                  key={idx}
                  origin={[width * idx, 0]}
                  color={each.stroke}
                  displayName={tooltipLabel}
                  value={yDisplayValue}
                  options={each}
                  forChart={chartId}
                  onClick={onClick}
                  fontFamily={fontFamily}
                  fontSize={fontSize}
                  textFill={textFill}
                  labelFill={labelFill}
                  bgFill={bgFill}
                  sourceLetter={sourceLetter}
                  showSourceDot={showSourceDot}
                  sourceHint={sourceHint}
                  data-evo-id="tooltip-moving-average-2"
                />
              );
            })
            .filter(Boolean) // Remove null entries
        }
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

MovingAverageTooltip.propTypes = {
  className: PropTypes.string,
  displayFormat: PropTypes.func.isRequired,
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  displayValuesFor: PropTypes.func,
  onClick: PropTypes.func,
  textFill: PropTypes.string,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  width: PropTypes.number,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      yAccessor: PropTypes.func.isRequired,
      type: PropTypes.string.isRequired,
      stroke: PropTypes.string.isRequired,
      windowSize: PropTypes.number.isRequired,
      sourcePath: PropTypes.string,
      echo: PropTypes.any,
    }),
  ),
};

MovingAverageTooltip.defaultProps = {
  className:
    "react-stockcharts-tooltip react-stockcharts-moving-average-tooltip",
  displayFormat: (d) => formatNumber(d, 2),
  displayValuesFor: displayValuesFor,
  origin: [0, 10],
  width: 85,
};

export default MovingAverageTooltip;
